import { useState, useCallback, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin } from "lucide-react";
import styles from "./CitiesManager.module.css";
import AddEditCityModal from "../../../components/GeoData/Cities/AddEditCityModal";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import SearchableSelect from "../../../components/BaseComponents/SearchableSelect/SearchableSelect";
import { useToast } from "../../../context/ToastContext";
import {
  useGetCities,
  useCreateCity,
  useUpdateCity,
  useDeleteCity,
} from "../../../api/geo/useCities";
import { useGetCountries } from "../../../api/geo/useCountries";
import { useGetRegions } from "../../../api/geo/useRegions";
import { assignHolidayTypesToCity } from "../../../api/geo/useHolidayTypes";
import { geoCascadeMessage } from "../../../utils/geoCascadeMessage";
import { parentOff } from "../../../utils/geoParentOff";

/** Map API city to row shape (and to modal initialData with marketing) */
const toRow = (c) => ({
  ...c,
  country: c.countryName,
  marketing: {
    title: c.title ?? "",
    para1: c.paragraph1 ?? "",
    para2: c.paragraph2 ?? "",
  },
});

// Pending toggle changes: { [rowId]: { active? } }
const getDisplayValue = (row, field, pendingChanges) =>
  pendingChanges[row.id]?.[field] ?? row[field];

const CityManager = () => {
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");

  // Sync filters from URL query params (e.g. when navigating from Countries or Regions page)
  useEffect(() => {
    const countryId = searchParams.get("countryId") || "";
    const regionId = searchParams.get("regionId") || "";
    setCountryFilter(countryId);
    setRegionFilter(regionId);
    if (countryId || regionId) setPage(1);
  }, [searchParams]);
  const [showAddEdit, setShowAddEdit] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});
  const [toggleSaveLoading, setToggleSaveLoading] = useState(false);

  const { data: citiesData, pagination, loading, error, refetch } = useGetCities({
    page,
    limit,
    search: search || undefined,
    countryId: countryFilter || undefined,
    regionId: regionFilter || undefined,
    sortBy: "name",
    sortOrder: "ASC",
  });

  // Every country, not the first page: there are ~250, and a 200 cap silently dropped the end of
  // the alphabet (United States, Uruguay, Zweden, …) from the filter — as on Airports.
  const { data: countriesData } = useGetCountries({
    page: 1,
    limit: 500,
    sortBy: "name",
    sortOrder: "ASC",
  });
  const { data: regionsData } = useGetRegions({
    page: 1,
    limit: 500,
    countryId: countryFilter || undefined,
    sortBy: "name",
    sortOrder: "ASC",
  });

  const { execute: createCity, loading: createLoading } = useCreateCity();
  const { execute: updateCity, loading: updateLoading } = useUpdateCity();
  const { execute: deleteCity, loading: deleteLoading } = useDeleteCity();

  const rows = (citiesData || []).map(toRow);
  const countries = (countriesData || []).map((c) => ({ id: c.id, name: c.name }));
  const regions = (regionsData || []).map((r) => ({ id: r.id, name: r.name }));
  const saveLoading = createLoading || updateLoading;
  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  const pageNumbers = useMemo(() => {
    const total = pagination?.totalPages ?? 1;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const current = page;
    const pages = new Set([1, total]);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (p > prev + 1) result.push("…");
      result.push(p);
      prev = p;
    }
    return result;
  }, [pagination?.totalPages, page]);

  const applyToggle = useCallback((row, field) => {
    setPendingChanges((prev) => {
      const current = prev[row.id] ?? {};
      const serverVal = row[field];
      const pendingVal = current[field];
      const nextVal = pendingVal !== undefined ? !pendingVal : !serverVal;
      return {
        ...prev,
        [row.id]: { ...current, [field]: nextVal },
      };
    });
  }, []);

  // Auto-search: debounce the input so the list filters as you type (no button).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
      setPendingChanges({});
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleAdd = () => {
    setMode("add");
    setSelectedRow(null);
    setShowAddEdit(true);
  };

  const handleEdit = (row) => {
    setMode("edit");
    setSelectedRow(row);
    setShowAddEdit(true);
  };

  const handleDeleteClick = (row) => setRowToDelete(row);

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      await deleteCity(rowToDelete.id);
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[rowToDelete.id];
        return next;
      });
      refetch();
      setRowToDelete(null);
      showToast("City deleted successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete city", "error");
    }
  };

  const handleSaveToggles = async () => {
    const ids = Object.keys(pendingChanges);
    if (ids.length === 0) return;
    setToggleSaveLoading(true);
    try {
      // Switching a city off closes every hotel in it, so tally what the API reports and say
      // so — silently deactivating hotels would be a nasty surprise.
      let switchedOff = 0;
      let restored = 0;
      for (const id of ids) {
        const patch = pendingChanges[id];
        const row = rows.find((r) => String(r.id) === String(id));
        const payload = { active: patch.active ?? row?.active };
        const res = await updateCity(id, payload);
        switchedOff += res?.hotelCascade?.switchedOff || 0;
        restored += res?.hotelCascade?.restored || 0;
      }
      setPendingChanges({});
      refetch();
      showToast(geoCascadeMessage({ switchedOff, restored }), "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save changes", "error");
    } finally {
      setToggleSaveLoading(false);
    }
  };

  const handleSave = async (payload) => {
    try {
      const { holidayTypeIds = [], ...cityPayload } = payload;

      if (mode === "add") {
        const res = await createCity(cityPayload);
        const newCityId = res?.data?.id ?? res?.id;
        if (newCityId != null && holidayTypeIds.length > 0) {
          await assignHolidayTypesToCity(newCityId, holidayTypeIds);
        }
        showToast("City created successfully", "success");
      } else if (selectedRow?.id) {
        await updateCity(selectedRow.id, cityPayload);
        await assignHolidayTypesToCity(selectedRow.id, holidayTypeIds);
        showToast("City updated successfully", "success");
      }
      setShowAddEdit(false);
      refetch();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to save city";
      showToast(msg, "error");
      throw new Error(msg);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Cities</div>
        <button className={styles.add} onClick={handleAdd}>
          + Add City
        </button>
      </div>

      <div className={styles.tagLine}>
        <span className={styles.tagIcon} aria-hidden>ℹ</span>
        <span>Cities define destinations for hotels and bookings. City codes help with search and mapping.</span>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search city or code..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <SearchableSelect
          className={styles.dropdownFilter}
          width={240}
          placeholder="Country: All"
          searchPlaceholder="Search country..."
          value={countryFilter}
          options={[
            { label: "Country: All", value: "" },
            ...countries.map((c) => ({ label: c.name, value: String(c.id) })),
          ]}
          onChange={(value) => {
            setCountryFilter(value);
            setRegionFilter("");
            setPage(1);
            setPendingChanges({});
          }}
        />
        <SearchableSelect
          className={styles.dropdownFilter}
          width={220}
          placeholder="Region: All"
          searchPlaceholder="Search region..."
          value={regionFilter}
          disabled={!countryFilter}
          options={[
            { label: "Region: All", value: "" },
            ...regions.map((r) => ({ label: r.name, value: String(r.id) })),
          ]}
          onChange={(value) => {
            setRegionFilter(value);
            setPage(1);
            setPendingChanges({});
          }}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading cities…</div>
      ) : (
        <>
          <div className={styles.resultsSummary}>
            Showing {rows.length} of {pagination?.total ?? 0} cities
          </div>
          <div className={styles.tableSection}>
            <div className={styles.tableCard}>
              <table className={styles.table}>
              <thead>
                <tr>
                  <th>City Name</th>
                  <th>City Code</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th>View Zones</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={styles.noResults}>
                      No cities found.
                    </td>
                  </tr>
                ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td className={styles.code}>{row.code}</td>
                    <td>{row.countryName ?? row.country}</td>
                    <td>
                      {parentOff(row) ? (
                        <>
                          {/* Off through its parent: shown off, own setting kept (applies again when the parent is ON). */}
                          <ToggleSwitch checked={false} disabled onChange={() => {}} />
                          <div style={{ fontSize: 11, color: "#8a8f98", marginTop: 4, whiteSpace: "nowrap" }}>
                            Off – {parentOff(row)} is off (own: {getDisplayValue(row, "active", pendingChanges) ? "ON" : "OFF"})
                          </div>
                        </>
                      ) : (
                        <ToggleSwitch
                          checked={!!getDisplayValue(row, "active", pendingChanges)}
                          onChange={() => applyToggle(row, "active")}
                        />
                      )}
                    </td>
                    <td>
                      <Link
                        to={`/geodata/zones?destinationId=${row.id}`}
                        className={styles.viewLink}
                      >
                        <MapPin size={14} aria-hidden />
                        <span>View zones</span>
                      </Link>
                    </td>
                    <td className={styles.rowActions}>
                      <button onClick={() => handleEdit(row)}>Edit</button>
                      <button onClick={() => handleDeleteClick(row)}>Delete</button>
                    </td>
                  </tr>
                )))}
              </tbody>
              </table>
            </div>

            <div className={styles.footerBar}>
              <div className={styles.pagination}>
                <span className={styles.paginationInfo}>
                  Page {pagination?.page ?? page} of {pagination?.totalPages || 1} ({pagination?.total ?? 0} total)
                </span>
                {(pagination?.totalPages ?? 1) > 1 && (
                  <div className={styles.pageNumbers}>
                    {pageNumbers.map((p, idx) =>
                      p === "…" ? (
                        <span key={`ellipsis-${idx}`} className={styles.ellipsis}>…</span>
                      ) : (
                        <button
                          key={p}
                          className={`${styles.pageBtn} ${Number(p) === Number(page) ? styles.pageBtnActive : ""}`}
                          onClick={() => {
                            setPage(p);
                            setPendingChanges({});
                          }}
                        >
                          {p}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                className={styles.saveChangesBtn}
                onClick={handleSaveToggles}
                disabled={!hasPendingChanges || saveLoading}
              >
                {toggleSaveLoading ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      )}

      <AddEditCityModal
        open={showAddEdit}
        onClose={() => setShowAddEdit(false)}
        mode={mode}
        initialData={selectedRow}
        countries={countries}
        onSave={handleSave}
        saveLoading={saveLoading}
      />

      <DeleteConfirmModal
        open={!!rowToDelete}
        onClose={() => setRowToDelete(null)}
        title="Delete city"
        itemName={rowToDelete?.name}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default CityManager;
